import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LOT_ORIGIN_LABELS_IT,
  LOT_STATUS_LABELS_IT,
  LOT_TARGET_COUNTRY_LABELS_IT,
  lotCreateRequestSchema,
  type LotCreateRequest,
  type LotGenealogyLink,
} from '@produzionejb/types';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@produzionejb/ui';
import { ApiError } from '../../lib/api-client';
import { useCreateLot, useLot, useLotGenealogy, useUpdateLot } from '../../lib/lots-api';
import { useProducts } from '../../lib/products-api';

const DEFAULT_VALUES: LotCreateRequest = {
  code: '',
  origin: 'OTHER',
  productId: '',
  status: 'ACTIVE',
  residualQty: 0,
  blockedReason: null,
  receivedAt: null,
  parentLotId: null,
  targetCountry: null,
};

export function LotFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const lotQ = useLot(id);
  const createMut = useCreateLot();
  const updateMut = useUpdateLot(id ?? '');

  // Carico tutti i prodotti ACTIVE per il dropdown (max 200 per ora)
  const productsQ = useProducts({ pageSize: 200, status: 'ACTIVE' });

  const form = useForm<LotCreateRequest>({
    resolver: zodResolver(lotCreateRequestSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const currentStatus = form.watch('status');

  useEffect(() => {
    if (isEdit && lotQ.data) {
      form.reset({
        code: lotQ.data.code,
        origin: lotQ.data.origin,
        productId: lotQ.data.productId,
        status: lotQ.data.status,
        residualQty: lotQ.data.residualQty,
        blockedReason: lotQ.data.blockedReason,
        receivedAt: lotQ.data.receivedAt,
        parentLotId: lotQ.data.parentLotId,
        targetCountry: lotQ.data.targetCountry,
      });
    }
  }, [isEdit, lotQ.data, form]);

  async function onSubmit(values: LotCreateRequest) {
    // Normalizza: empty string da datepicker -> null
    const payload: LotCreateRequest = {
      ...values,
      receivedAt: values.receivedAt ? new Date(values.receivedAt).toISOString() : null,
      blockedReason: values.blockedReason?.trim() ? values.blockedReason : null,
      parentLotId: values.parentLotId ?? null,
    };
    try {
      if (isEdit && id) {
        await updateMut.mutateAsync(payload);
      } else {
        await createMut.mutateAsync(payload);
      }
      navigate('/lots');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        form.setError('code', { message: t('lots.form.errorDuplicateCode') });
      } else {
        window.alert((err as Error).message);
      }
    }
  }

  const isSubmitting = createMut.isPending || updateMut.isPending;

  if (isEdit && lotQ.isLoading) {
    return <p className="text-sm text-slate-500">{t('common.loading')}</p>;
  }
  if (isEdit && lotQ.error) {
    return (
      <p className="text-sm text-red-600">
        {t('lots.form.errorLoad')}: {(lotQ.error as Error).message}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">
          {isEdit ? t('lots.form.titleEdit') : t('lots.form.titleNew')}
        </h2>
        <Button variant="secondary" onClick={() => navigate('/lots')}>
          {t('common.back')}
        </Button>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Card>
          <CardHeader>
            <CardTitle>{t('lots.form.sectionIdentity')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="code">{t('lots.fields.code')} *</Label>
              <Input
                id="code"
                autoComplete="off"
                placeholder="DE001010526"
                {...form.register('code')}
              />
              <p className="text-xs text-slate-500">{t('lots.form.codeHint')}</p>
              {form.formState.errors.code && (
                <p className="text-xs text-red-600">{form.formState.errors.code.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="origin">{t('lots.fields.origin')}</Label>
              <select
                id="origin"
                className="block h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                {...form.register('origin')}
              >
                {Object.entries(LOT_ORIGIN_LABELS_IT).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="productId">{t('lots.fields.product')} *</Label>
              <select
                id="productId"
                className="block h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                {...form.register('productId')}
                disabled={productsQ.isLoading}
              >
                <option value="">{t('lots.form.selectProductPlaceholder')}</option>
                {productsQ.data?.items.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} — {p.name}
                  </option>
                ))}
              </select>
              {form.formState.errors.productId && (
                <p className="text-xs text-red-600">{form.formState.errors.productId.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('lots.form.sectionState')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="status">{t('lots.fields.status')}</Label>
              <select
                id="status"
                className="block h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                {...form.register('status')}
              >
                {Object.entries(LOT_STATUS_LABELS_IT).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="residualQty">{t('lots.fields.residualQty')}</Label>
              <Input
                id="residualQty"
                type="number"
                step="0.0001"
                min="0"
                {...form.register('residualQty', { valueAsNumber: true })}
              />
              <p className="text-xs text-slate-500">{t('lots.form.qtyHint')}</p>
              {form.formState.errors.residualQty && (
                <p className="text-xs text-red-600">
                  {form.formState.errors.residualQty.message}
                </p>
              )}
            </div>
            {currentStatus === 'BLOCKED' && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="blockedReason">{t('lots.fields.blockedReason')} *</Label>
                <textarea
                  id="blockedReason"
                  rows={2}
                  className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  {...form.register('blockedReason')}
                />
                {form.formState.errors.blockedReason && (
                  <p className="text-xs text-red-600">
                    {form.formState.errors.blockedReason.message}
                  </p>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="receivedAt">{t('lots.fields.receivedAt')}</Label>
              <Input
                id="receivedAt"
                type="date"
                {...form.register('receivedAt', {
                  setValueAs: (v) =>
                    !v ? null : new Date(v).toISOString(),
                })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="targetCountry">{t('lots.fields.targetCountry')}</Label>
              <select
                id="targetCountry"
                className="block h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                {...form.register('targetCountry', {
                  setValueAs: (v) => (v === '' ? null : v),
                })}
              >
                <option value="">{t('lots.form.targetCountryNone')}</option>
                {Object.entries(LOT_TARGET_COUNTRY_LABELS_IT).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500">{t('lots.form.targetCountryHint')}</p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={() => navigate('/lots')}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isEdit ? t('common.save') : t('lots.form.createButton')}
          </Button>
        </div>
      </form>

      {isEdit && id ? <LotGenealogyCard id={id} /> : null}
    </div>
  );
}

// Genealogia del lotto: origine (lotti da cui deriva) + derivati (lotti generati),
// ricavati dai documenti PSC/LNP via GET /api/lots/:id/genealogy. Sola lettura.
function LotGenealogyCard({ id }: { id: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const genQ = useLotGenealogy(id);
  const g = genQ.data;
  const hasAny = !!g && (g.parents.length > 0 || g.children.length > 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('lots.genealogy.title', 'Genealogia del lotto')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-500">
          {t(
            'lots.genealogy.hint',
            'Ricavata dai documenti PSC/LNP chiusi: da quali lotti deriva questo lotto e in quali lotti è confluito.',
          )}
        </p>
        {genQ.isLoading && <p className="text-sm text-slate-500">{t('common.loading')}</p>}
        {g && !hasAny && (
          <p className="text-sm text-slate-500">
            {t(
              'lots.genealogy.none',
              'Nessuna trasformazione da/verso altri lotti (lotto grezzo, o cernita sullo stesso lotto).',
            )}
          </p>
        )}
        {g && g.parents.length > 0 && (
          <div className="space-y-1.5">
            <Label>{t('lots.genealogy.parents', 'Origine — deriva dai lotti')}</Label>
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
              {g.parents.map((p) => (
                <GenealogyRow
                  key={`p-${p.lotId}-${p.documentId}`}
                  link={p}
                  onLot={() => navigate(`/lots/${p.lotId}/edit`)}
                />
              ))}
            </ul>
          </div>
        )}
        {g && g.children.length > 0 && (
          <div className="space-y-1.5">
            <Label>{t('lots.genealogy.children', 'Derivati — confluito nei lotti')}</Label>
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
              {g.children.map((c) => (
                <GenealogyRow
                  key={`c-${c.lotId}-${c.documentId}`}
                  link={c}
                  onLot={() => navigate(`/lots/${c.lotId}/edit`)}
                />
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GenealogyRow({ link, onLot }: { link: LotGenealogyLink; onLot: () => void }) {
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
      <button type="button" onClick={onLot} className="font-medium text-blue-700 hover:underline">
        {link.lotCode}
      </button>
      <span className="flex-1 truncate text-slate-600">
        {link.productName ?? link.productCode ?? '—'}
      </span>
      <span className="tabular-nums text-slate-700">{link.qty.toLocaleString('it-IT')} g</span>
      <span className="text-xs text-slate-400">
        {link.documentType} {link.documentCode ?? ''}
      </span>
    </li>
  );
}
